import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, fmtErr } from "../lib/pos";

/**
 * Masked 4-digit manager PIN modal.
 *
 * Usage:
 *   const [ask, setAsk] = useState(null); // { percent } while asking
 *   <ManagerPinModal
 *     open={!!ask}
 *     percent={ask?.percent}
 *     onCancel={() => setAsk(null)}
 *     onVerified={() => { setAsk(null); applyDiscount(ask.percent); }}
 *   />
 *
 * Behaviour:
 *  - Input is `type="password"` so digits are masked as ••••.
 *  - Autofocus on open, Enter submits, Escape cancels.
 *  - Verifies via POST /settings/verify-pin; surfaces backend errors inline.
 */
export function ManagerPinModal({ open, percent, onCancel, onVerified }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin(""); setErr(""); setBusy(false);
      // Focus after paint so the modal is on screen
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (pin.length !== 4) { setErr("PIN must be 4 digits"); return; }
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/settings/verify-pin`, { pin: pin.trim() });
      if (r.data?.ok) onVerified?.();
      else setErr("Incorrect PIN. Discount not applied.");
    } catch (e) { setErr(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="modal-backdrop"
      data-testid="manager-pin-backdrop"
      onClick={(e) => e.target.classList.contains("modal-backdrop") && onCancel?.()}
    >
      <div className="modal" style={{ width: "min(360px, 92vw)" }} data-testid="manager-pin-modal">
        <div className="section-line">
          <h2 style={{ fontSize: 22 }}>Manager PIN</h2>
          <button data-testid="manager-pin-close" onClick={onCancel}>×</button>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          Enter the 4-digit PIN to authorise a <b style={{ color: "#fff" }}>{percent}% discount</b>.
        </p>
        <input
          ref={inputRef}
          data-testid="manager-pin-input"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          value={pin}
          placeholder="••••"
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onCancel?.();
          }}
          style={{ fontSize: 22, letterSpacing: 12, textAlign: "center", padding: "14px 12px" }}
        />
        {err && <p style={{ color: "var(--red)", fontSize: 12, margin: 0 }} data-testid="manager-pin-error">{err}</p>}
        <button
          data-testid="manager-pin-submit"
          className="primary wide"
          disabled={busy || pin.length !== 4}
          onClick={submit}
        >{busy ? "Verifying…" : "Authorise"}</button>
      </div>
    </div>
  );
}
